import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
import { useProductGateActions } from 'datastage/store/productGate/useProductGate';
import { datastageApi } from 'datastage/api';
import { filterCriteria } from 'datastage/utils/filterCriteria';

// Minimum time to show the loading spinner (in ms) for consistent UX
const MIN_LOADING_DURATION = 400;

/**
 * Custom hook that consolidates product gate data fetching logic.
 * Handles loading states with minimum duration, error toasts, and Redux updates.
 *
 * This pattern ensures:
 * - Consistent loading spinner behavior (minimum 400ms display)
 * - Centralized error handling
 * - Reusable fetch logic across components
 *
 * @returns {Object} Object containing fetchProductGates function and product gate state values
 */
export function useProductGateData() {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();
    const { updateProductGate } = useProductGateActions();
    const { filterForm, productGateRange, productGateSort, itemsPerPage, espnTitle } = useSelector(
        (s) => s.productGate,
    );

    /**
     * Fetches product gate data from the API with the given range.
     * Ensures loading spinner is visible for at least MIN_LOADING_DURATION.
     *
     * @param {Array} range - The pagination range array (e.g., [0, 19])
     * @param {Object|null} customFilterData - Optional filter data to override Redux filterForm
     * @param {Array|null} customSort - Optional sort array (e.g., ["id", "DESC"]) to override Redux productGateSort
     * @param {boolean|null} customEspnTitle - Optional ESPN title flag to override Redux espnTitle
     * @returns {Promise<Object|null>} The API response data or null on error
     */
    const fetchProductGates = useCallback(
        async (range, customFilterData = null, customSort = null, customEspnTitle = null) => {
            const startTime = Date.now();
            setLoadingTrue();

            const filterData = customFilterData ?? filterForm;
            const sortData = customSort ?? productGateSort;
            const espnTitleData = customEspnTitle ?? espnTitle;

            try {
                const data = await datastageApi.rightsLineProductUpdates(
                    filterCriteria(filterData, espnTitleData, 'productUpdates'),
                    range,
                    sortData,
                );

                // Check for error response (has code field) or null (network/auth error)
                if (!data || data.code) {
                    showErrorToast(data?.message || 'Failed to fetch product updates.');
                    return null;
                }

                return data;
            } catch (error) {
                // This shouldn't happen since API layer handles all errors
                // But handle it just in case
                showErrorToast(error?.message || 'Unexpected error fetching product updates.');
                console.error('Unexpected error fetching product updates:', error);
                return null;
            } finally {
                // Ensure minimum loading duration for consistent UX
                const elapsed = Date.now() - startTime;
                const remainingTime = MIN_LOADING_DURATION - elapsed;

                if (remainingTime > 0) {
                    setTimeout(() => setLoadingFalse(), remainingTime);
                } else {
                    setLoadingFalse();
                }
            }
        },
        [filterForm, productGateSort, espnTitle, setLoadingTrue, setLoadingFalse, showErrorToast],
    );

    return {
        fetchProductGates,
        updateProductGate,
        itemsPerPage,
        productGateRange,
        filterForm,
        productGateSort,
        espnTitle,
    };
}
